import DataClass from './DataClass';
import FormHelper from './FormHelper';
import Moment from 'moment';

/**
 * LeaveRequest class for mapping form data to leave request structure
 * Maps raw form submission data to the expected table format
 */
class LeaveRequest extends DataClass {

	constructor(props, mentorGroups = [], leaveRequest = null) {
		super(props);
		this.availableMentorGroups = mentorGroups; // Store available mentor groups for lookup
		this.mentorGroups = []; // Initialize assigned mentor groups list
		this.student = null; // Initialize student property
		this.leaveRequest = leaveRequest || null; // store associated leaveRequest object when available
		this.mapData();
	}

	/**
	 * Converts UTC date/time to local time while preserving full days
	 * If a leave starts or ends partway through a day, the entire day is counted as leave
	 * For startDate: converts to start of day in local time
	 * For endDate: converts to end of day in local time
	 * @param {string|Date|Moment} utcDate - The UTC date to convert
	 * @param {string} dateType - 'start' or 'end' to determine time of day
	 * @returns {string} ISO string of the converted date in local time
	 */
	static convertUtcToLocalDate(utcDate, dateType = 'start') {
		if (!utcDate) return utcDate;

		// Parse the UTC date
		const utcMoment = Moment.utc(utcDate);
		if (!utcMoment.isValid()) return utcDate;

		// Convert to local time
		const localMoment = utcMoment.local();

		// // For startDate: set to start of day (00:00:00)
		// if (dateType === 'start') {
		// 	return localMoment.startOf('day').toISOString();
		// }

		// // For endDate: set to end of day (23:59:59)
		// if (dateType === 'end') {
		// 	return localMoment.endOf('day').toISOString();
		// }

		return localMoment.toISOString();
	}

	/**
	 * Maps raw form data to expected leave request structure
	 */
	mapData() {
		const raw = this.data;

		// Basic mapping from raw form data
		this.id = this.leaveRequest.id;
		this.status = this.mapStatus(this.leaveRequest.status);
		const rawStatus = this.leaveRequest?.status;
		this.normalizedStatus = rawStatus == null ? null : rawStatus.toString().trim().toUpperCase();
		this.receivedDate = this.leaveRequest.created;
		this.decisionSent = raw?.decisionSent || false;

		// Convert dates from UTC to local time
		this.startDate = this.leaveRequest.startDate ?
			LeaveRequest.convertUtcToLocalDate(this.leaveRequest.startDate, 'start') :
			null;
		this.endDate = this.leaveRequest.endDate ?
			LeaveRequest.convertUtcToLocalDate(this.leaveRequest.endDate, 'end') :
			null;

		this.student = this.mapUser(this.leaveRequest.student);
		this.decision = this.leaveRequest?.status;
		this.decisionComment = this.leaveRequest?.decisionComment;
		console.log(this.leaveRequest?.sections);
		this.mentorGroups = this.leaveRequest?.sections;

		if (raw != null) {
			// Extract data from submitted leave request instance for dates and reason
			const submittedInstance = this.getSubmittedLeaveRequestInstance(raw.formInstances);
			if (submittedInstance) {
				// Extract dates and reason from field results using template
				const fieldData = this.extractRequestFieldData(submittedInstance.fieldResults, submittedInstance.formTemplate?.fields);

				// Convert extracted dates from UTC to local time
				this.startDate = fieldData.startDate ?
					LeaveRequest.convertUtcToLocalDate(fieldData.startDate, 'start') :
					this.startDate;
				this.endDate = fieldData.endDate ?
					LeaveRequest.convertUtcToLocalDate(fieldData.endDate, 'end') :
					this.endDate;

				this.reason = fieldData.reason;
				this.creator = this.mapUser(submittedInstance.user);
			}
		}
	}

	/**
	 * Maps status from various formats to standardized English format
	 * Returns English keys that should be translated in the component
	 */
	mapStatus(status) {
		if (!status) return 'Pending decision';
		const normalizedStatus = (status || '').toString().toLowerCase().trim();

		// If status contains keywords, try to map them
		if (normalizedStatus.includes('pending')) {
			return 'Pending decision';
		}
		if (normalizedStatus.includes('approved')) {
			return 'Approved';
		}
		if (normalizedStatus.includes('rejected')) {
			return 'Rejected';
		}
		if (normalizedStatus.includes('revoked')) {
			return 'Withdrawn';
		}

		return status;
	}

	/**
	 * Maps student/user information to expected format
	 */
	mapUser(userData) {
		if (!userData) return null;

		// Check if user data has actual values or just nulls
		if (userData.firstName === null && userData.lastName === null &&
			userData.displayName === null && userData.userName === null) {
			return null; // No actual user data available
		}

		return {
			firstName: userData.firstName || userData.givenName || userData.displayName || '',
			lastName: userData.lastName || userData.familyName || userData.surname || '',
			userId: userData.id || userData.userId || null,
		};
	}

	/**
	 * Gets a submitted form instance by template type from formInstances array
	 * @param {Array} formInstances - Array of form instances to search through
	 * @param {string} templateType - The form template type to search for (e.g., 'LEAVE_REQUEST', 'LEAVE_RESPONSE')
	 * @returns {Object|null} The submitted instance matching the template type or null if not found
	 */
	getSubmittedFormInstanceByType(formInstances, templateType) {
		if (!Array.isArray(formInstances)) return null;
		// Find the instance with status "SUBMITTED" and matching template type
		return formInstances.find(instance => {
			const hasCorrectType = instance.formTemplate?.type == templateType;
			const isSubmitted = instance.status == 'SUBMITTED';
			return hasCorrectType && isSubmitted;
		}) || null;
	}

	/**
	 * Gets the submitted leave request instance (from the requester/student)
	 */
	getSubmittedLeaveRequestInstance(formInstances) {
		return this.getSubmittedFormInstanceByType(formInstances, 'LEAVE_REQUEST');
	}

	/**
	 * Gets the submitted leave response instance (from the responder/decision maker)
	 */
	getSubmittedLeaveResponseInstance(formInstances) {
		return this.getSubmittedFormInstanceByType(formInstances, 'LEAVE_RESPONSE');
	}

	getNoteInstanceForUser(formInstances, user) {
		if (!Array.isArray(formInstances)) return null;

		// Get mentor group IDs from this instance's mentor groups
		const mentorGroupIds = this.availableMentorGroups.map(group => group.graphId);
		const userId = user.getUserId();

		let userAssignedInstance = null;
		let mentorGroupAssignedInstance = null;
		let permissionLevelInstance = null;

		// Iterate through all LEAVE_RESPONSE instances and categorize by priority
		formInstances.forEach(instance => {
			// Only look at LEAVE_RESPONSE type instances
			if (instance.formTemplate?.type !== 'LEAVE_RESPONSE') return;

			// Priority 1: Check if instance is directly assigned to current user
			if (instance.assignedTo === userId) {
				if (!userAssignedInstance) {
					userAssignedInstance = instance;
				}
				return; // Skip lower priority checks
			}

			// Priority 2: Check if instance belongs to user's mentor groups
			if (instance.assignedToType === 'MENTOR_GROUP' && mentorGroupIds.includes(instance.assignedTo)) {
				if (!mentorGroupAssignedInstance) {
					mentorGroupAssignedInstance = instance;
				}
				return; // Skip lower priority checks
			}

			// Priority 3: User has school-level permission
			if (user.havingPermission("LEAVES_EDIT_SCHOOL")) {
				if (!permissionLevelInstance) {
					permissionLevelInstance = instance;
				}
			}
		});

		// Return the highest priority instance found
		return userAssignedInstance || mentorGroupAssignedInstance || permissionLevelInstance || null;
	}

	/**
	 * Gets both direct and indirect decision instances for a user
	 * Direct: instance assigned to user or their mentor group
	 * Indirect: instance accessible via permission level (not directly assigned)
	 * @param {Array} formInstances - Array of form instances to search through
	 * @param {Object} user - The current user object
	 * @returns {Object} Object with directDecisionInstance and indirectDecisionInstance properties
	 */
	getDecisionInstancesForUser(formInstances, user) {
		if (!Array.isArray(formInstances)) {
			return { directDecisionInstance: null, indirectDecisionInstance: null };
		}

		const userId = user.getUserId();

		let userAssignedInstance = null;
		let permissionLevelInstance = null;

		// Iterate through all LEAVE_RESPONSE instances and categorize by type
		formInstances.forEach(instance => {
			// Only look at LEAVE_RESPONSE type instances
			if (instance.formTemplate?.type !== 'LEAVE_RESPONSE') return;

			// Check if instance is directly assigned to current user
			if (instance.assignedTo === userId) {
				if (!userAssignedInstance) {
					userAssignedInstance = instance;
				}
				return; // This is a direct assignment
			}
		});

		// Direct instance prioritizes user assignment, then mentor group
		const directDecisionInstance = userAssignedInstance || null;

		// Indirect instance is only available if user has permission and no direct assignment
		const indirectDecisionInstance = directDecisionInstance ? null : permissionLevelInstance;

		return {
			directDecisionInstance,
			indirectDecisionInstance
		};
	}

	/**
	 * Gets the decision instance that belongs to a specific user or their mentor groups
	 * Prioritizes: 1) directly assigned to user, 2) assigned to mentor group, 3) permission level
	 * @param {Array} formInstances - Array of form instances to search through
	 * @param {Object} user - The current user object
	 * @returns {Object|null} The decision instance for the user or null if not found
	 */
	getDecisionInstanceForUser(formInstances, user) {
		const { directDecisionInstance, indirectDecisionInstance } = this.getDecisionInstancesForUser(formInstances, user);
		return directDecisionInstance || indirectDecisionInstance || null;
	}

	/**
	 * Extracts field data from leave request fieldResults array using FormTemplate fields
	 * Automatically identifies fields based on their types
	 */
	extractRequestFieldData(fieldResults, templateFields) {
		let startDate = null;
		let endDate = null;
		let reason = '';

		if (!Array.isArray(fieldResults) || !Array.isArray(templateFields)) {
			return { startDate, endDate, reason };
		}

		// Use FormHelper to extract all fields based on template
		const extracted = FormHelper.extractFieldsFromResults(fieldResults, templateFields);

		// Find the date range field (FIELDS_START_END_INPUTS type)
		const dateRangeField = templateFields.find(field => field.type === 'FIELDS_START_END_INPUTS');
		if (dateRangeField && extracted[dateRangeField.name]) {
			startDate = extracted[dateRangeField.name]?.start || null;
			endDate = extracted[dateRangeField.name]?.end || null;
		}

		// Find the reason/text field (FIELDS_INPUT_TEXTAREA type)
		const textAreaField = templateFields.find(field => field.type === 'FIELDS_INPUT_TEXTAREA');
		if (textAreaField && extracted[textAreaField.name]) {
			reason = extracted[textAreaField.name] || '';
		}

		return { startDate, endDate, reason };
	}

	/**
	 * Extracts decision field data from leave response fieldResults array using FormTemplate fields
	 * Automatically identifies decision and comment fields based on their types
	 */
	extractDecisionFieldData(fieldResults, templateFields) {
		let decision = null;
		let comment = '';

		if (!Array.isArray(fieldResults) || !Array.isArray(templateFields)) {
			return { decision, comment };
		}

		// Use FormHelper to extract all fields based on template
		const extracted = FormHelper.extractFieldsFromResults(fieldResults, templateFields);

		// Find the decision field (FIELDS_INPUT_DECISION_RADIOGROUP type)
		const decisionField = templateFields.find(field => field.type === 'FIELDS_INPUT_DECISION_RADIOGROUP');
		if (decisionField && extracted[decisionField.name]) {
			decision = extracted[decisionField.name];
		}

		// Find the comment/text field (FIELDS_INPUT_TEXTAREA type)
		const textAreaField = templateFields.find(field => field.type === 'FIELDS_INPUT_TEXTAREA');
		if (textAreaField && extracted[textAreaField.name]) {
			comment = extracted[textAreaField.name] || '';
		}

		return { decision, comment };
	}


	/**
	 * Finds the mentor group associated with a form instance
	 * For MENTOR_GROUP type forms, matches by group ID or other identifiers
	 */
	findMentorGroup(formInstance, availableMentorGroups) {
		if (!formInstance || !Array.isArray(availableMentorGroups)) {
			return [];
		}

		// Try to match by groupId if available in formInstance
		if (formInstance.assignedTo) {
			const matchedGroup = availableMentorGroups.find(group =>
				group.graphId == formInstance.assignedTo
			);
			if (matchedGroup) {
				return [this.mapMentorGroup(matchedGroup)];
			}
		}

		// If no specific match found, return empty array (no mentor group assigned)
		return [];
	}

	/**
	 * Maps a single mentor group to expected format
	 */
	mapMentorGroup(group) {
		if (!group) return null;

		const id = group.id ?? group.graphId ?? group.graphGroupId ?? group.sectionId ?? null;
		const graphId = group.graphId ?? group.graphGroupId ?? group.sectionId ?? id;
		const title = group.title ?? group.name ?? group.groupName ?? group.displayName ?? '';
		const name = group.name ?? group.title ?? group.groupName ?? group.displayName ?? title;

		return {
			id,
			graphId,
			title,
			name,
		};
	}

	/**
	 * Returns the leave request data in table row format
	 */
	toTableRow() {
		return {
			id: this.id,
			student: this.student, // Object with firstName, lastName
			receivedDate: this.receivedDate,
			startDate: this.startDate,
			mentorGroups: this.mentorGroups,
			status: this.status,
			decisionSent: this.decisionSent,
			// Additional fields that might be useful
			endDate: this.endDate,
			reason: this.reason,
			decision: this.decision,
			decisionComment: this.decisionComment,
			normalizedStatus: this.normalizedStatus,
		};
	}

	/**
	 * Map leaveRequests array together with raw forms array.
	 * Only returns forms that exist in rawForms and are referenced by a leaveRequest (by formId/FormId).
	 * @param {Array} leaveRequests - array of leave request objects that reference forms (has formId or FormId)
	 */
	static toTableRows(leaveRequests = []) {
		if (!Array.isArray(leaveRequests)) return [];

		return leaveRequests
			.filter(request => request != null)
			.map(request => new LeaveRequest(null, [], request).toTableRow());
	}

	/**
	 * Extracts the decision value from form field results
	 * @param {Array} fieldResults - Array of field result objects from the decision form
	 * @param {Array} templateFields - Array of field definitions from the form template
	 * @returns {string|null} The decision value or null if not found
	 */
	static extractDecisionValue(fieldResults, templateFields) {
		if (!Array.isArray(fieldResults) || !Array.isArray(templateFields)) {
			return null;
		}

		// Find the decision field (radio group field)
		const decisionField = fieldResults.find(field =>
			templateFields.some(f =>
				f.name === field.fieldName && f.type === 'FIELDS_INPUT_DECISION_RADIOGROUP'
			)
		);

		return decisionField?.value.toUpperCase() || null;
	}

	/**
	 * Maps a decision value to a form status
	 * @param {string} decision - The decision value from the form
	 * @returns {string} The mapped form status ('approved', 'rejected', or 'pending')
	 */
	static mapDecisionToFormStatus(decision) {
		if (!decision) return 'PENDING';

		const normalizedDecision = decision.toLowerCase();

		// Check for approved variants
		if (normalizedDecision.includes('approved')) {
			return 'APPROVED';
		}

		// Check for rejected/denied variants
		if (normalizedDecision.includes('denied')) {
			return 'REJECTED';
		}

		// Default to pending
		return 'PENDING';
	}

}

export default LeaveRequest;